import { http, HttpResponse } from "msw";

import {
  SESSION_WHOSE_CHART_FAILS,
  DOWNLOAD_FAILS,
  EXPENSE_DOWNLOAD_FAILS,
  addCdrsToExpense,
  approveReport,
  getChargeRecord,
  getChartMeterData,
  getChartSummary,
  getExpenseDetails,
  getReport,
  listNonLinkedCdrs,
  listPriorityTransactions,
  listReports,
  removeCdrsFromExpense,
} from "../data/reimbursements";

/**
 * REGISTRATION ORDER IS LOAD-BEARING. `non-linked-items` is a single path segment
 * after `expense-reports`, so it also matches the `:reportId` pattern; it must be
 * registered first or a candidate-list request is answered with a report lookup.
 */

const EXPENSE_REPORTS = "*/proxy/company-car-orchestrator/expense-reports";

const DOCUMENT_STORE = "http://tech-api.e2e.invalid/public/documents";

function buildReportDocument(reportId: number, separator: string): string {
  const rows = [
    ["Report", "Payee", "Account", "Consumption", "Amount"],
    [
      String(reportId),
      "Bo Kristensen",
      "DK5000400440116243",
      "455.000",
      "738.00",
    ],
  ];
  return rows.map((row) => row.join(separator)).join("\n");
}

const reimbursementsHandlers = [
  http.get(`${DOCUMENT_STORE}/:fileName`, ({ params }) =>
    HttpResponse.text(`%PDF-1.4 ${String(params.fileName)}`, {
      headers: { "Content-Type": "application/pdf" },
    })
  ),

  http.get(`${EXPENSE_REPORTS}/non-linked-items`, ({ request }) => {
    const url = new URL(request.url);
    return HttpResponse.json(
      listNonLinkedCdrs(
        Number(url.searchParams.get("page") ?? 1),
        Number(url.searchParams.get("limit") ?? 24)
      )
    );
  }),

  http.get(`${EXPENSE_REPORTS}/expenses/:expenseId/document`, ({ params }) => {
    if (params.expenseId === EXPENSE_DOWNLOAD_FAILS.id) {
      return new HttpResponse(null, { status: 500 });
    }
    return HttpResponse.json(
      `http://tech-api.e2e.invalid/public/documents/${String(params.expenseId)}.pdf`
    );
  }),

  http.get(`${EXPENSE_REPORTS}/expenses/:expenseId`, ({ params }) => {
    const details = getExpenseDetails(String(params.expenseId));
    return details
      ? HttpResponse.json(details)
      : new HttpResponse(null, { status: 404 });
  }),

  http.get(`${EXPENSE_REPORTS}/:reportId/document`, ({ params, request }) => {
    const reportId = Number(params.reportId);
    if (reportId === DOWNLOAD_FAILS.id) {
      return new HttpResponse(null, { status: 500 });
    }

    const url = new URL(request.url);
    const format = url.searchParams.get("format") ?? "pdf";
    const separator = url.searchParams.get("separator") ?? ";";

    // The real endpoint answers a presigned URL for a PDF and the document body
    // for a CSV, and the client branches on which. Modelling only the CSV shape
    // here is what hid a PDF that saved the URL text as the file.
    if (format !== "csv") {
      return HttpResponse.json(
        `${DOCUMENT_STORE}/FleetExpenseReport-${String(reportId)}.pdf`
      );
    }

    return HttpResponse.text(buildReportDocument(reportId, separator), {
      headers: {
        "Content-Type": "text/csv",
        "X-E2E-Separator": separator,
      },
    });
  }),

  http.get(`${EXPENSE_REPORTS}/:reportId`, ({ params }) => {
    const report = getReport(Number(params.reportId));
    return report
      ? HttpResponse.json(report)
      : new HttpResponse(null, { status: 404 });
  }),

  ...[EXPENSE_REPORTS, `${EXPENSE_REPORTS}/`].map((path) =>
    http.get(path, ({ request }) => {
      const url = new URL(request.url);
      return HttpResponse.json(
        listReports(
          Number(url.searchParams.get("page") ?? 1),
          Number(url.searchParams.get("limit") ?? 24)
        )
      );
    })
  ),

  http.post(`${EXPENSE_REPORTS}/:reportId/approve`, ({ params }) =>
    approveReport(Number(params.reportId))
      ? new HttpResponse(null, { status: 204 })
      : new HttpResponse(null, { status: 404 })
  ),

  http.post(
    `${EXPENSE_REPORTS}/expenses/:expenseId/charge-detail-records/add`,
    async ({ params, request }) => {
      const body = (await request.json()) as { transactionIds?: number[] };
      return addCdrsToExpense(
        String(params.expenseId),
        body.transactionIds ?? []
      )
        ? new HttpResponse(null, { status: 204 })
        : new HttpResponse(null, { status: 404 });
    }
  ),

  http.post(
    `${EXPENSE_REPORTS}/expenses/:expenseId/charge-detail-records/remove`,
    async ({ params, request }) => {
      const body = (await request.json()) as { transactionIds?: number[] };
      return removeCdrsFromExpense(
        String(params.expenseId),
        body.transactionIds ?? []
      )
        ? new HttpResponse(null, { status: 204 })
        : new HttpResponse(null, { status: 404 });
    }
  ),

  http.get("*/charge-record/v2/chart/:transactionId", ({ params }) => {
    const transactionId = Number(params.transactionId);
    if (transactionId === SESSION_WHOSE_CHART_FAILS.transactionId) {
      return new HttpResponse(null, { status: 500 });
    }
    return HttpResponse.json({
      meterData: getChartMeterData(transactionId),
      ...getChartSummary(),
    });
  }),

  http.get("*/charge-record/v2/:segment", ({ params, request }) => {
    const segment = String(params.segment);

    if (!/^\d+$/.test(segment)) {
      const url = new URL(request.url);
      if (url.searchParams.get("priority") === "1") {
        return HttpResponse.json(listPriorityTransactions());
      }
      return HttpResponse.json({
        data: [],
        pagination: { currentPage: 1, itemCount: 0, itemsPerPage: 24 },
      });
    }

    const record = getChargeRecord(Number(segment));
    return record
      ? HttpResponse.json(record)
      : new HttpResponse(null, { status: 404 });
  }),
];

export { reimbursementsHandlers };
